/**
 * useTvLogin — State machine hook for the TV / QR login confirmation flow.
 *
 * States: idle → loading → confirming → idle  (or → error)
 */
import { useState, useCallback } from "react";
import Cookies from "js-cookie";
import { useAuth } from "@/providers/authProvider";
import { scanQRCode, confirmQRCode, cancelQRCode } from "@/services/qrService";
import { trackGtagEvent } from "@/services/gtagAnalyticsService";

export function useTvLogin() {
    const { user } = useAuth();
    const [status, setStatus] = useState("idle"); // idle | loading | confirming | error
    const [deviceInfo, setDeviceInfo] = useState(null); // { device, ip, qrId, sessionId }
    const [error, setError] = useState(null);

    /** Get auth payload from cookies + user context */
    const getAuthPayload = useCallback(() => {
        const accessToken = Cookies.get("userAccessToken");
        const mobileNumber = user?.mobileNumber;
        return { mobileNumber: Number(mobileNumber), accessToken };
    }, [user]);

    /** Submit a 6-char temporary code */
    const handleCodeSubmit = useCallback(
        async (code) => {
            setStatus("loading");
            setError(null);
            try {
                const { mobileNumber, accessToken } = getAuthPayload();
                if (!accessToken || !mobileNumber) {
                    throw new Error("ابتدا باید وارد حساب خود شوید");
                }

                const res = await scanQRCode({
                    temporaryCode: code.toLowerCase(),
                    mobileNumber,
                    accessToken,
                });

                // res could be the data directly (unwrapped) — contains device, ip, qrId, sessionId
                setDeviceInfo({
                    device: res.device,
                    ip: res.ip,
                    qrId: res.qrId,
                    sessionId: res.sessionId,
                    userInfo: res.userInfo,
                });
                trackGtagEvent("qr_login_scanned", {
                    source: "tv_login_modal",
                    device: res.device,
                    has_ip: Boolean(res.ip),
                });
                setStatus("confirming");
            } catch (e) {
                setError(e.message || "خطا در ارسال کد");
                setStatus("error");
            }
        },
        [getAuthPayload]
    );

    /** Confirm the login on the requesting device */
    const handleConfirm = useCallback(async () => {
        if (!deviceInfo) return { success: false };
        setStatus("loading");
        try {
            const { mobileNumber, accessToken } = getAuthPayload();
            const res = await confirmQRCode({
                qrId: deviceInfo.qrId,
                sessionId: deviceInfo.sessionId,
                mobileNumber,
                accessToken,
            });
            setStatus("idle");
            return { success: true, message: res.message || "ورود با موفقیت انجام شد" };
        } catch (e) {
            setError(e.message || "خطا در تأیید ورود");
            setStatus("error");
            return { success: false };
        }
    }, [deviceInfo, getAuthPayload]);

    /** Cancel / reject the login request */
    const handleCancel = useCallback(async () => {
        if (deviceInfo) {
            try {
                await cancelQRCode(deviceInfo.qrId, deviceInfo.sessionId);
            } catch {
                // Ignore cancel errors
            }
        }
        setStatus("idle");
        setDeviceInfo(null);
        setError(null);
    }, [deviceInfo]);

    /** Full reset (used when closing the modal) */
    const reset = useCallback(() => {
        setStatus("idle");
        setDeviceInfo(null);
        setError(null);
    }, []);

    return {
        status,
        deviceInfo,
        error,
        handleCodeSubmit,
        handleConfirm,
        handleCancel,
        reset,
    };
}
